// In-browser verifying preview of a just-built site (szzt-spec.md §14 "Preview").
//
// Preview renders the built manifest and resources "through an in-browser verifying path with per-
// resource CID checks, never persisted, no served origin, not installable" (§14). Rather than fork a
// second verifying path, this REUSES the `@szzt/gateway` serve engine: it translates a local
// `BuildResult` into the same `SzztIndex` the gateway serves from signed state, and feeds the
// built blob bytes through the same `VerifyingCache` + `serve`. So preview exercises the exact
// routing (§9.9), header (§9.4/§9.6), and re-verification (§9.4) logic that production serving does —
// there is only one serving/verifying implementation, and it is the gateway's.
//
// The difference from production is only the trust SOURCE: production resolves the index from signed
// PDS records and re-derives the root CID against them (`resolveIndex`), whereas preview's index is
// the local build the user just produced — there is no PDS, no signature, no network. Verification
// still runs (each blob is re-hashed against the manifest's `src` CID), which is what makes preview a
// faithful dry run and catches a build that would fail to serve.

import type { BuildResult } from "@szzt/core";
import { serve, VerifyingCache } from "@szzt/gateway";
import type {
  CidLocation,
  Fetcher,
  SzztIndex,
  ResourceLocation,
  ServeDecision,
  ServeMode,
  ServeRequest,
} from "@szzt/gateway";

/**
 * Placeholder owner DID for a preview index. Preview never contacts a PDS (§14), so this value is
 * only a stable key threaded through the gateway `Fetcher` seam; the in-memory fetcher ignores it.
 */
export const PREVIEW_DID = "did:szzt:preview";

/**
 * Translate a local {@link BuildResult} into a gateway {@link SzztIndex}. Mirrors
 * `resolveIndex` (the signed-state path), but sources the MASL from the trusted local build and maps
 * each resource CID to its just-built bytes — no re-derivation-vs-signed-pins step, because there are
 * no signed pins yet. The root CID resolves to the build's canonical dCBOR42 manifest bytes.
 */
export function previewIndex(built: BuildResult, did: string = PREVIEW_DID): SzztIndex {
  const masl = built.manifest;
  const resources = new Map<string, ResourceLocation>();
  const cidToLocation = new Map<string, CidLocation>();

  for (const [path, entry] of Object.entries(masl.resources)) {
    const cid = entry.src.ref.$link;
    const contentType = entry["content-type"];
    const size = entry.src.size;
    const loc: ResourceLocation = {
      path,
      cid,
      size,
      contentType,
      ...(entry["content-security-policy"] !== undefined
        ? { csp: entry["content-security-policy"] }
        : {}),
      ...(entry.sourcemap !== undefined ? { sourcemap: entry.sourcemap } : {}),
    };
    resources.set(path, loc);
    if (!cidToLocation.has(cid)) {
      cidToLocation.set(cid, { kind: "blob", did, cid, size, contentType, path });
    }
  }
  cidToLocation.set(built.rootCid, { kind: "root", cid: built.rootCid, bytes: built.encodedManifest });

  return {
    did,
    rootCid: built.rootCid,
    name: masl.name,
    ...(masl.routing !== undefined ? { routing: masl.routing } : {}),
    resources,
    cidToLocation,
  };
}

/**
 * An in-memory {@link Fetcher} over a build's blobs — the transport seam the {@link VerifyingCache}
 * pulls bytes from during preview. There is no network: bytes come straight from the build, and the
 * cache still re-hashes them against the manifest CID (so a corrupt build surfaces as a fail-closed
 * 502, exactly as in production).
 */
export function previewFetcher(built: BuildResult): Fetcher {
  const byCid = new Map<string, Uint8Array>();
  for (const blob of built.blobs) byCid.set(blob.cid, blob.bytes);
  return {
    async getBlob(_did: string, cid: string): Promise<Uint8Array> {
      const bytes = byCid.get(cid);
      if (bytes === undefined) throw new Error(`preview: no built blob for CID ${cid}`);
      return bytes;
    },
  };
}

/** A reusable verifying-preview context: a fixed index + a warm-across-requests verifying cache. */
export interface Preview {
  index: SzztIndex;
  cache: VerifyingCache;
  /** Serve one request through the gateway engine (routing + verify + headers), in site or tile mode. */
  serve(req: ServeRequest, mode?: ServeMode): Promise<ServeDecision>;
}

/**
 * Create a verifying-preview context for a build. The returned `serve` runs the full gateway pipeline
 * (routing §9.9, re-verification §9.4, site/tile headers §9.4/§9.6) against the local build; the
 * cache persists verified bytes across requests, as a real service worker would.
 */
export function createPreview(built: BuildResult, did: string = PREVIEW_DID): Preview {
  const index = previewIndex(built, did);
  const cache = new VerifyingCache(previewFetcher(built));
  return {
    index,
    cache,
    serve: (req: ServeRequest, mode: ServeMode = "site") => serve(index, cache, req, mode),
  };
}

/** One-shot convenience: build a fresh preview context and serve a single request through it. */
export function servePreview(
  built: BuildResult,
  req: ServeRequest,
  mode: ServeMode = "site",
): Promise<ServeDecision> {
  return createPreview(built).serve(req, mode);
}

/**
 * The inputs for a sandboxed preview iframe (§14): the value for its `sandbox` attribute and the HTML
 * to mount via `srcdoc`. `sandbox` is the literal `"allow-scripts"` — scripts run, but the frame gets
 * an OPAQUE origin with no access to the parent (the publish UI), so just-built, unsigned content is
 * isolated from the origin holding the OAuth session, exactly as §14 requires.
 */
export interface PreviewFrame {
  sandbox: "allow-scripts";
  srcdoc: string;
}

/**
 * Serve the preview root (or `path`) through the verifying engine and produce the {@link PreviewFrame}
 * a publish UI mounts as `<iframe sandbox="allow-scripts">`. Pure and DOM-free: this is the load-
 * bearing sandboxing decision, kept here (in the unit-tested module) rather than in the typecheck-only
 * `browser.ts` glue that applies it to a real `HTMLIFrameElement` (`mountPreview`).
 */
export async function renderPreviewFrame(
  preview: Preview,
  path = "/",
  mode: ServeMode = "site",
): Promise<PreviewFrame> {
  const decision = await preview.serve({ path, navigation: true }, mode);
  const body = decision.body;
  const srcdoc = typeof body === "string" ? body : body ? new TextDecoder().decode(body) : "";
  return { sandbox: "allow-scripts", srcdoc };
}
